import React, { useState, useEffect, useRef } from 'react';

import useActiveLayersStore from 'stores/useSelectedLayersStore';
import homeService from 'services/homeService';
import { clearZSTChanges, handleZST } from 'utils/rightBarButtonsFunctionalities';

import { notification } from 'antd';

import MapSection from 'features/MapSection';
import HomeUI from 'features/HomeUI';

import 'assets/styles/variables.css';
import 'assets/styles/Home/HomeUI/home.css';
import 'assets/styles/Home/HomeUI/menu.css';
import 'assets/styles/Home/base-map-and-nuts.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'leaflet/dist/leaflet.css';
import "react-datepicker/dist/react-datepicker.css";
import { useNotificationHandler } from 'hooks/useNotificationHandler';

function Home() {

  useEffect(() => {

    mapRef.current.on('draw:drawstart', (event) => { // When drawing is started, checks that the marker isn't activated.
      if (markerModeRef.current) handleMarker();
    });
    
  }, []);

  const mapRef = useRef(null);

  const [startDate, setStartDate] = useState(null); // Set the start date state to todays date
  const [enabledDates, setEnabledDates] = useState([]); // set the enabled dates state to be an empty array
  const [predictionDay, setPredictionDay] = useState(0); // Saves the number of added days   for the prediction of meteorological data.   

  const [mapParameters, setMapParameters] = useState([]); // Set state of mapParametars

  const { contextHolder } = useNotificationHandler();
  const markerModeRef = useRef(null);
  const [isMarkerButtonClicked, setIsMarkerButtonClicked] = useState(false); //Aditional useState that has the same value as markermoderef to update the button color

  const tscModeRef = useRef(null);
  const [tscCoords, setTscCoords] = useState({ lat: '', lng: '' });

  const [isTSCButtonClicked, setIsTSCButtonClicked] = useState(false);
  const [isTSRButtonClicked, setIsTSRButtonClicked] = useState(false);
  const [isTSAButtonClicked, setIsTSAButtonClicked] = useState(false);
  const layerRefs = useRef([]);
  const [isDatePickerDisabled, setIsDatePickerDisabled] = useState(true);
  const [ZSTPopUpContent, setZSTPopUpContent] = useState(null); //When assigned a value, the pop up is shown

  const handleTimeSeriesButtonClick = (action) => {

    if (markerModeRef.current) {
      handleMarker();
    }

    if (ZSTPopUpContent) {
      clearZSTChanges(mapRef, setZSTPopUpContent);
    }

    switch (action) {
      case "tsc": {
        setIsTSCButtonClicked(prev => !prev);
        break;
      }

      case "tsr": {
        setIsTSRButtonClicked(prev => !prev);
        break;
      }

      case "tsa": {
        setIsTSAButtonClicked(prev => !prev);
        break;
      }

      default: return;
    }

  }

  function handleMarker() {

    markerModeRef.current = !markerModeRef.current;
    setIsMarkerButtonClicked(markerModeRef.current);

    if (ZSTPopUpContent) {
      setZSTPopUpContent(null);
    }

    const mapElement = document.getElementById('map');
    if (mapElement) {
      if (markerModeRef.current) {
        mapElement.classList.add('crosshair-cursor');
      } else {
        mapElement.classList.remove('crosshair-cursor');
      }
    }

  }

  const fetchCommonDates = async (newMapParameters) => {
    try {

      const params = [];
      newMapParameters.forEach((layerParameters) => {
        params.push({
          folder: layerParameters.provider,
          timeResolution: layerParameters.timeResolution,
          spaceResolution: layerParameters.spaceResolution
        });
      });

      // Make the API call to the new endpoint using homeService.getCommonDates
      return await homeService.getCommonDates(params); // Expecting filtered_data array in response.

    } catch (error) {
      notification.error({
        message: 'Error fetching dates',
        description: 'Something unexpected happened while trying to fetch the dates: ' + error.message,
        placement: 'top',
        style: { backgroundColor: '#fff', color: '#000', borderRadius: '6px' },
      });
      console.error('Error fetching common dates:', error);

    }
  };

  useEffect(() => {
    if (!isTSCButtonClicked) {
      mapRef.current.closePopup();
    }
  }, [isTSCButtonClicked]);

  return (

    <div className="app-container">
      { contextHolder }

      <HomeUI
        mapRef={mapRef}
        ZSTPopUpContent={ZSTPopUpContent}
        setZSTPopUpContent={setZSTPopUpContent}
        startDate={startDate}
        setStartDate={setStartDate}
        layerRefs={layerRefs}
        isDatePickerDisabled={isDatePickerDisabled}
        setIsDatePickerDisabled={setIsDatePickerDisabled}
        tscCoords={tscCoords}
        tscModeRef={tscModeRef}
        fetchCommonDates={fetchCommonDates}
        setMapParameters={setMapParameters}
        setEnabledDates={setEnabledDates}
        isButtonClicked={{
          tsa: isTSAButtonClicked, tsc: isTSCButtonClicked, tsr: isTSRButtonClicked
        }}
        setIsButtonClicked={{
          tsa: () => handleTimeSeriesButtonClick('tsa'),
          tsc: () => handleTimeSeriesButtonClick('tsc'),
          tsr: () => handleTimeSeriesButtonClick('tsr')
        }}
      />

      <MapSection
        startDate={startDate}
        setStartDate={setStartDate}
        mapParameters={mapParameters}
        setMapParameters={setMapParameters}
        markerModeRef={markerModeRef}
        layerRefs={layerRefs}
        setTscCoords={setTscCoords}
        enabledDates={enabledDates}
        isDatePickerDisabled={isDatePickerDisabled}
        setIsDatePickerDisabled={setIsDatePickerDisabled}
        tscModeRef={tscModeRef}
        isButtonClicked={{
          tsa: isTSAButtonClicked, tsc: isTSCButtonClicked, tsr: isTSRButtonClicked, marker: isMarkerButtonClicked
        }}
        onClick={{
          tsa: () => handleTimeSeriesButtonClick('tsa'),
          tsc: () => handleTimeSeriesButtonClick('tsc'),
          tsr: () => handleTimeSeriesButtonClick('tsr'),
          zst: () => {
            if (markerModeRef.current) handleMarker();
            handleZST(mapRef, mapParameters, startDate, setZSTPopUpContent)
          },
          marker: () => handleMarker()
        }}
        mapRef={mapRef}
        predictionDayProps={{ predictionDay, setPredictionDay }}
        setEnabledDates={setEnabledDates}
        fetchCommonDates={fetchCommonDates}
        setZSTPopUpContent={setZSTPopUpContent}
      />

    </div>

  );
};

export default Home;